import { computed, ref, type Ref } from "vue";
import { useStore } from "vuex";
import {
  type DocumentSpace,
  useCreateDocumentSpaceVoucherMutation,
} from "@/graphql/operations";
import {
  fetchCRMConsentStatus,
  hasCommunicationOptIn,
  isSharedSpaceConsentGateError,
  resolveSharedSpaceLinkPrefix,
  resolveSharedSpaceUrlPrefixes,
  SharedSpaceConsentGateError,
} from "@/util";

type UseDocumentSpaceSharingArgs = {
  documentSpace: Ref<DocumentSpace | undefined>;
  emailVerificationValid: Ref<boolean>;
};

export const useDocumentSpaceSharing = ({
  documentSpace,
  emailVerificationValid,
}: UseDocumentSpaceSharingArgs) => {
  const store = useStore();
  const user = store.getters.user;
  const checkingConsent = ref(false);
  let activeConsentCheck: symbol | undefined;
  const canUseSharedSpaces = computed(() => user.canUseSharedSpaces());
  const fallbackPrefix = `${window.location.origin}/2/`;
  const prefixes = computed(() =>
    resolveSharedSpaceUrlPrefixes(user.mergedConfig, fallbackPrefix),
  );
  const sharedSpaceUrlPrefix = computed(
    () => prefixes.value.sharedSpaceUrlPrefix,
  );
  const defaultSpacePrefix = computed(() => prefixes.value.defaultSpacePrefix);
  const hasBusinessCardVariants = computed(
    () => prefixes.value.hasBusinessCardVariants,
  );

  const crmContact = computed(
    () => documentSpace.value?.documentSpaceCrmMapping?.crmContact,
  );

  const needsEmail = computed(
    () => !!documentSpace.value?.config?.requiresEmailAuthentication,
  );

  const displayEmailVerification = computed(
    () =>
      canUseSharedSpaces.value &&
      (needsEmail.value || user.hasEmailVerificationOption()),
  );

  const hasValidEmail = computed(
    () => !needsEmail.value || emailVerificationValid.value,
  );

  const displayShareOptions = computed(
    () =>
      canUseSharedSpaces.value &&
      hasValidEmail.value &&
      (!user.hasCRMIntegration() ||
        crmContact.value !== undefined ||
        !user.hasRequiredCRMContactForSharing()),
  );

  const { executeMutation: createVoucher } =
    useCreateDocumentSpaceVoucherMutation();

  const ensureCanCreateVoucher = async (
    isPreview: boolean,
    signal?: AbortSignal,
  ) => {
    if (isPreview || !user.hasRequiredCRMConsentForSharing()) {
      return;
    }

    const contact = crmContact.value;
    if (!contact?.id) {
      throw new SharedSpaceConsentGateError("missing");
    }

    const check = Symbol();
    activeConsentCheck = check;
    checkingConsent.value = true;
    const finishChecking = () => {
      if (activeConsentCheck === check) {
        activeConsentCheck = undefined;
        checkingConsent.value = false;
      }
    };
    signal?.addEventListener("abort", finishChecking, { once: true });
    try {
      const consent = await fetchCRMConsentStatus(contact.id);
      if (!hasCommunicationOptIn(consent)) {
        throw new SharedSpaceConsentGateError("missing");
      }
    } catch (err) {
      if (isSharedSpaceConsentGateError(err)) {
        throw err;
      }
      throw new SharedSpaceConsentGateError("check_failed");
    } finally {
      signal?.removeEventListener("abort", finishChecking);
      finishChecking();
    }
  };

  const ensureDocumentSpaceId = () => {
    const spaceId = documentSpace.value?.id;
    if (!spaceId) {
      throw new Error("Document space ID is not available.");
    }

    return spaceId;
  };

  const generateVoucherSegment = async (
    isPreview: boolean = false,
    linkId?: string,
    signal?: AbortSignal,
  ) => {
    signal?.throwIfAborted();
    if (!canUseSharedSpaces.value) {
      throw new Error("Shared Spaces are disabled.");
    }
    const spaceId = ensureDocumentSpaceId();
    await ensureCanCreateVoucher(isPreview, signal);
    signal?.throwIfAborted();
    const result = await createVoucher({
      id: spaceId,
      campaignPhaseLinkId: linkId,
      isPreview,
    });
    signal?.throwIfAborted();
    if (result.error) {
      throw new Error("Failed to generate share link.");
    }
    const urlSegment =
      result.data?.pvCreateVoucherForDocumentSpace?.result?.urlSegment;
    if (!urlSegment) {
      throw new Error("Failed to generate share link.");
    }

    return urlSegment;
  };

  const generateLink = async (
    urlPrefix?: string,
    isPreview: boolean = false,
    linkId?: string,
    signal?: AbortSignal,
  ) => {
    const urlSegment = await generateVoucherSegment(isPreview, linkId, signal);
    const resolvedPrefix = resolveSharedSpaceLinkPrefix(
      urlPrefix,
      sharedSpaceUrlPrefix.value,
    );
    return `${resolvedPrefix}${urlSegment}`;
  };

  const copyLink = async (
    urlPrefix?: string,
    linkId?: string,
    isPreview: boolean = false,
    signal?: AbortSignal,
  ) => {
    const link = await generateLink(urlPrefix, isPreview, linkId, signal);
    signal?.throwIfAborted();
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      throw new Error("Failed to copy link to clipboard.");
    }
    return link;
  };

  return {
    crmContact,
    needsEmail,
    displayEmailVerification,
    displayShareOptions,
    checkingConsent,
    sharedSpaceUrlPrefix,
    defaultSpacePrefix,
    hasBusinessCardVariants,
    generateLink,
    generateVoucherSegment,
    copyLink,
  };
};
